import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { movieInfo, featuredReviews, recommendedMovies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";

function Rating({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-500">
      <i className="ri-star-fill text-[11px]" />
      {value.toFixed(1)}
    </span>
  );
}

export default function Home() {
  return (
    <>
      <HeroCarousel />

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2 space-y-8 md:space-y-10">
            <div className="rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <Link to="/watch" className="group block relative w-full aspect-video overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=cinematic%20film%20still%20of%20a%20dim%20elegant%20room%20with%20red%20curtain%20and%20warm%20amber%20light%2C%20dramatic%20moody%20period%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=1280&height=720&seq=home-play-area&orientation=landscape"
                  alt="《色戒》在线播放演示"
                  title="色戒 在线观看"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-background-50/45 group-hover:bg-background-50/30 transition-colors" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                    <i className="ri-play-fill text-3xl md:text-4xl" />
                  </span>
                </span>
                <span className="absolute top-4 left-4 px-3 py-1 rounded-md bg-background-50/85 text-xs text-foreground-900 backdrop-blur-sm">
                  高清演示 · 仅作展示
                </span>
              </Link>
              <div className="p-4 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    {movieInfo.title}
                    <span className="ml-3 align-middle text-sm font-normal text-secondary-500">
                      {movieInfo.englishTitle}
                    </span>
                  </h2>
                  <p className="mt-2 text-xs md:text-sm text-secondary-500">
                    {movieInfo.year} · {movieInfo.region} · {movieInfo.genre} · {movieInfo.duration}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold text-accent-500">{movieInfo.rating}</p>
                    <p className="text-[11px] text-secondary-500">综合评分</p>
                  </div>
                  <Link
                    to="/watch"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line text-lg" />
                    开始播放
                  </Link>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
              <SectionHeading title="剧情摘要" subtitle="一段关于信念、情感与代价的时代故事" />
              <p className="mt-5 text-sm md:text-[15px] leading-loose text-foreground-700">{movieInfo.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {movieInfo.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/story"
                className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent-500 hover:text-accent-400 transition-colors whitespace-nowrap"
              >
                查看更多剧情
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div>
              <SectionHeading
                title="编辑精选影评"
                subtitle="来自影迷与影评人的深度解读"
                action={
                  <Link
                    to="/reviews"
                    className="inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-accent-500 transition-colors whitespace-nowrap"
                  >
                    全部影评
                    <i className="ri-arrow-right-line" />
                  </Link>
                }
              />
              <div className="mt-6 space-y-4">
                {featuredReviews.map((review) => (
                  <Link
                    key={review.id}
                    to={`/reviews/detail/${review.id}`}
                    className="group flex flex-col sm:flex-row gap-4 md:gap-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500 p-3 md:p-4 transition-colors"
                  >
                    <div className="w-full sm:w-48 shrink-0 aspect-video sm:aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={review.cover}
                        alt={review.title}
                        title={`${review.title} - 色戒影评`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 text-xs text-secondary-500">
                        <Rating value={review.rating} />
                        <span className="w-px h-3 bg-background-300" />
                        <span>{review.author}</span>
                        <span className="hidden sm:inline">{review.date}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
                        {review.title}
                      </h3>
                      <p className="mt-2 text-xs md:text-sm text-secondary-500 line-clamp-2 leading-relaxed">
                        {review.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-8">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  <h3 className="font-heading text-lg font-bold text-foreground-950">热门资讯</h3>
                </div>
                <Link to="/news" className="text-xs text-secondary-500 hover:text-accent-500 transition-colors">
                  更多
                </Link>
              </div>
              <ul className="mt-4 divide-y divide-background-200">
                {newsList.slice(0, 5).map((news) => (
                  <li key={news.id}>
                    <Link to="/news" className="group block py-3">
                      <span className="flex items-center gap-2 text-[11px] text-secondary-500">
                        <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">{news.category}</span>
                        <span>{news.date}</span>
                      </span>
                      <p className="mt-1.5 text-sm text-foreground-800 group-hover:text-accent-500 transition-colors line-clamp-2 leading-relaxed">
                        {news.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                <h3 className="font-heading text-lg font-bold text-foreground-950">推荐专题</h3>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3">
                {[
                  { icon: "ri-film-line", title: "经典华语电影专题", desc: "那些年我们一起看过的电影", to: "/reviews" },
                  { icon: "ri-quill-pen-line", title: "深度影评合辑", desc: "从镜头语言读到人性深处", to: "/reviews" },
                  { icon: "ri-newspaper-line", title: "影视幕后档案", desc: "还原一个时代的制作故事", to: "/news" },
                ].map((item) => (
                  <Link
                    key={item.title}
                    to={item.to}
                    className="group flex items-center gap-3 rounded-md bg-background-50 border border-background-200 hover:border-accent-500 p-3 transition-colors"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-accent-500 shrink-0">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-950 group-hover:text-accent-500 transition-colors truncate">
                        {item.title}
                      </span>
                      <span className="block text-xs text-secondary-500 truncate">{item.desc}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-14 md:mt-20">
          <SectionHeading title="相关电影推荐" subtitle="同样值得一看的剧情与情感佳作（演示示例）" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {recommendedMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}